import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FaExternalLinkAlt, FaSearch, FaShoppingCart, FaTimes } from 'react-icons/fa';

import {
  createStorePosOrder,
  defaultGroceryImage,
  fetchGroceryCategories,
  fetchGroceryOrderDetail,
  fetchSupermarketGroceryItems,
  findGroceryItemByBarcode,
  formatGroceryRupee,
  getGroceryItemPriceParts,
  getStoreGroceryImageUrl,
  looksLikeScannedBarcode,
  updateGroceryOrderStatus,
} from './groceryApi';
import GroceryCardPrice, { GroceryCardPackQty } from './GroceryCardPrice';
import SearchableSelect from './SearchableSelect';
import { getActiveStoreId } from './entityContext';
import { canAccessOrdersTab } from './rbacSession';
import StoreActiveOrders from './StoreActiveOrders';
import OrderConfirmation from './OrderConfirmation';
import './css/BuyGrocery.css';
import './css/PlaceOrder.css';

const TAX_PERCENT = 8;
const PAYMENT_METHODS = ['cash', 'card', 'upi', 'gpay', 'paytm', 'phonepe'];

function detailValue(value) {
  const text = value == null ? '' : String(value).trim();
  return text || '—';
}

const StorePos = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const storeId = getActiveStoreId();
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);
  const createdById = Number(user?.userId || user?.id || user?.User_Id || 0);

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scanFeedback, setScanFeedback] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const scanFeedbackTimerRef = useRef(null);
  const [cart, setCart] = useState({});
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [showPaymentTypeDropdown, setShowPaymentTypeDropdown] = useState(false);
  const [resumeOrderId, setResumeOrderId] = useState(null);
  const [resumeOrderRef, setResumeOrderRef] = useState('');
  const [resumeOrderLoading, setResumeOrderLoading] = useState(false);
  const [showMaximizeModal, setShowMaximizeModal] = useState(false);
  const [maximizeSearchQuery, setMaximizeSearchQuery] = useState('');
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [orderConfirmationData, setOrderConfirmationData] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const paymentTypeDropdownRef = useRef(null);
  const isCompletingSavedOrder = resumeOrderId != null;

  const mapCartLineItems = () =>
    cartLines.map((line) => ({
      groceryItemId: line.groceryItemId,
      itemName: line.itemName,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      unitId: line.unitId,
      gstPercent: TAX_PERCENT,
    }));

  const loadCatalog = useCallback(async () => {
    if (!storeId) {
      setItems([]);
      setError('No store selected. Choose one from the profile menu.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const [rows, cats] = await Promise.all([
        fetchSupermarketGroceryItems(storeId),
        fetchGroceryCategories(),
      ]);
      const categoryById = Object.fromEntries(
        (Array.isArray(cats) ? cats : []).map((c) => [
          String(c.groceryCategoryId),
          c.categoryName || 'Category',
        ])
      );
      const activeItems = (Array.isArray(rows) ? rows : [])
        .filter((row) => {
          const status = String(row.status || '').toUpperCase();
          const stock = Number(row.stockQuantity);
          return status !== 'INACTIVE' && (!Number.isFinite(stock) || stock > 0);
        })
        .map((row) => ({
          ...row,
          groceryCategoryName:
            row.groceryCategoryName ||
            categoryById[String(row.groceryCategoryId)] ||
            'General',
        }));
      setItems(activeItems);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load grocery catalog.');
      setItems([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    const completeOrderId = location.state?.completeOrderId;
    if (!completeOrderId) return undefined;

    let cancelled = false;
    const loadSavedOrder = async () => {
      setResumeOrderLoading(true);
      setError('');
      try {
        const order = await fetchGroceryOrderDetail(completeOrderId);
        if (cancelled) return;
        if (!order) {
          alert('Order not found.');
          return;
        }

        const cartMap = {};
        (order.lineItems || []).forEach((line) => {
          const key = String(line.groceryItemId);
          cartMap[key] = {
            groceryItemId: line.groceryItemId,
            itemName: line.itemName,
            unitPrice: Number(line.unitPrice) || 0,
            quantity: Number(line.quantity) || 0,
          };
        });

        setCart(cartMap);
        setCustomerName(order.receiverName || '');
        setCustomerPhone(order.receiverPhone || '');
        if (order.receiverName || order.receiverPhone) {
          setShowCustomerDetails(true);
        }
        setResumeOrderId(order.groceryOrderId ?? order.orderId ?? completeOrderId);
        setResumeOrderRef(
          order.displayOrderNo ||
            order.display_order_no ||
            order.displayOrderRef ||
            String(completeOrderId)
        );
        navigate('/home?tab=pos', { replace: true, state: {} });
      } catch (e) {
        if (!cancelled) {
          alert(e?.response?.data?.message || 'Could not load order for completion.');
        }
      } finally {
        if (!cancelled) setResumeOrderLoading(false);
      }
    };

    loadSavedOrder();
    return () => {
      cancelled = true;
    };
  }, [location.state?.completeOrderId, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        paymentTypeDropdownRef.current &&
        !paymentTypeDropdownRef.current.contains(event.target)
      ) {
        setShowPaymentTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const categoryOptions = useMemo(
    () =>
      categories.map((c) => ({
        id: String(c.groceryCategoryId),
        name: c.categoryName || 'Category',
      })),
    [categories]
  );

  const categorySelectOptions = useMemo(
    () => [
      { value: 'all', label: 'All categories' },
      ...categoryOptions.map((cat) => ({ value: cat.id, label: cat.name })),
    ],
    [categoryOptions]
  );

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter((item) => {
      const catId = item.groceryCategoryId != null ? String(item.groceryCategoryId) : '';
      const catName = item.groceryCategoryName || '';
      const matchesCategory =
        activeCategory === 'all' ||
        catId === activeCategory ||
        catName === activeCategory;
      if (!matchesCategory) return false;
      if (!q) return true;
      const haystack = [item.itemName, item.barcode, item.groceryCategoryName, item.unitName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, searchQuery, activeCategory]);

  const maximizeFilteredItems = useMemo(() => {
    const q = maximizeSearchQuery.trim().toLowerCase();
    if (!q) return [];
    return items.filter((item) => {
      const haystack = [item.itemName, item.barcode, item.groceryCategoryName, item.unitName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, maximizeSearchQuery]);

  const cartLines = useMemo(() => Object.values(cart), [cart]);
  const cartCount = cartLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const subtotal = cartLines.reduce(
    (sum, line) => sum + Number(line.quantity || 0) * Number(line.unitPrice || 0),
    0
  );
  const gst = Math.round(subtotal * TAX_PERCENT) / 100;
  const total = subtotal + gst;

  const addToCart = (item) => {
    const key = String(item.groceryItemId);
    setCart((prev) => ({
      ...prev,
      [key]: {
        groceryItemId: item.groceryItemId,
        itemName: item.itemName,
        unitPrice: Number(item.sellingPrice) || 0,
        unitId: item.unitId,
        quantity: (prev[key]?.quantity ?? 0) + 1,
      },
    }));
  };

  const showScanFeedback = useCallback((type, message) => {
    if (scanFeedbackTimerRef.current) {
      clearTimeout(scanFeedbackTimerRef.current);
    }
    setScanFeedback({ type, message });
    scanFeedbackTimerRef.current = setTimeout(() => {
      setScanFeedback(null);
      scanFeedbackTimerRef.current = null;
    }, 2500);
  }, []);

  useEffect(
    () => () => {
      if (scanFeedbackTimerRef.current) {
        clearTimeout(scanFeedbackTimerRef.current);
      }
    },
    []
  );

  /** USB/handheld scanners type digits then Enter into the focused search box. */
  const handleBarcodeScanSubmit = useCallback(
    (rawCode, { clearQuery } = {}) => {
      const code = String(rawCode || '').trim();
      if (!code) return false;

      const matched = findGroceryItemByBarcode(items, code);
      if (matched) {
        addToCart(matched);
        clearQuery?.();
        showScanFeedback('success', `Added ${matched.itemName || 'item'} to cart`);
        return true;
      }

      if (looksLikeScannedBarcode(code)) {
        showScanFeedback('error', `No item found for barcode ${code}`);
        clearQuery?.();
        return true;
      }

      return false;
    },
    [items, showScanFeedback]
  );

  const handleSearchKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleBarcodeScanSubmit(searchQuery, {
      clearQuery: () => setSearchQuery(''),
    });
  };

  const handleMaximizeSearchKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleBarcodeScanSubmit(maximizeSearchQuery, {
      clearQuery: () => setMaximizeSearchQuery(''),
    });
  };

  const updateQuantity = (groceryItemId, nextQty) => {
    const key = String(groceryItemId);
    if (nextQty <= 0) {
      setCart((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setCart((prev) => ({
      ...prev,
      [key]: { ...prev[key], quantity: nextQty },
    }));
  };

  const removeFromCart = (groceryItemId) => {
    updateQuantity(groceryItemId, 0);
  };

  const clearCart = () => {
    setCart({});
  };

  const resetAfterOrder = () => {
    clearCart();
    setCustomerName('');
    setCustomerPhone('');
    setShowCustomerDetails(false);
    setPaymentType('');
    setShowPaymentTypeDropdown(false);
    setShowMaximizeModal(false);
    setMaximizeSearchQuery('');
    setResumeOrderId(null);
    setResumeOrderRef('');
  };

  const buildPosConfirmationData = ({
    orderId,
    orderNumber,
    isNewOrderPlaced,
    paymentMethod,
    lines,
    totals,
  }) => ({
    orderId,
    orderNumber: orderNumber || (orderId != null ? String(orderId) : ''),
    mobile: customerPhone.trim() || null,
    tableNumber: null,
    paymentMethod,
    items: lines.map((line) => ({
      foodItemName: line.itemName,
      name: line.itemName,
      foodQuantity: line.quantity,
      quantity: line.quantity,
      price: Number(line.unitPrice) || 0,
    })),
    subtotal: totals.subtotal,
    gst: totals.gst,
    gstPercentage: TAX_PERCENT,
    total: totals.total,
    isNewOrderPlaced,
    orderType: 'PICKUP',
    returnTab: 'pos',
  });

  const showPosPaymentSuccess = ({
    orderId,
    orderNumber,
    isNewOrderPlaced,
    paymentMethod,
    lines,
    totals,
  }) => {
    setOrderConfirmationData(
      buildPosConfirmationData({
        orderId,
        orderNumber,
        isNewOrderPlaced,
        paymentMethod,
        lines,
        totals,
      })
    );
    setShowOrderConfirmation(true);
  };

  const submitOrder = async (orderStatusCode, { requirePayment = false } = {}) => {
    if (!storeId) {
      alert('Select a store first.');
      return;
    }
    if (!createdById) {
      alert('Please sign in again.');
      return;
    }
    if (cartLines.length === 0) {
      alert('Please add items to cart');
      return;
    }
    if (requirePayment && (!paymentType || paymentType.trim() === '')) {
      alert('Please select a payment type');
      return;
    }

    try {
      setIsSubmitting(true);
      const snapshotLines = [...cartLines];
      const snapshotTotals = { subtotal, gst, total };
      const snapshotPayment = paymentType;

      if (isCompletingSavedOrder) {
        const data = await updateGroceryOrderStatus(resumeOrderId, {
          orderStatusCode: requirePayment ? 'CMP' : 'NEW',
          updatedBy: createdById,
          lineItems: mapCartLineItems(),
          ...(customerName.trim() ? { receiverName: customerName.trim() } : {}),
          ...(customerPhone.trim() ? { receiverPhone: customerPhone.trim() } : {}),
          ...(requirePayment
            ? {
                paymentType: snapshotPayment,
                amount: total,
                deliveredDate: new Date().toISOString(),
              }
            : {}),
        });
        if (requirePayment) {
          showPosPaymentSuccess({
            orderId: resumeOrderId,
            orderNumber: resumeOrderRef || String(resumeOrderId),
            isNewOrderPlaced: false,
            paymentMethod: snapshotPayment,
            lines: snapshotLines,
            totals: snapshotTotals,
          });
          resetAfterOrder();
          return;
        }
        alert(data?.message || 'Order saved successfully');
        resetAfterOrder();
        return;
      }

      const data = await createStorePosOrder({
        storeId,
        orderStatusCode,
        createdBy: createdById,
        receiverName: customerName.trim() || undefined,
        receiverPhone: customerPhone.trim() || undefined,
        grandTotal: total,
        paymentType: requirePayment ? snapshotPayment : undefined,
        lineItems: cartLines.map((line) => ({
          groceryItemId: line.groceryItemId,
          itemName: line.itemName,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          unitId: line.unitId,
          gstPercent: TAX_PERCENT,
        })),
      });

      if (requirePayment) {
        showPosPaymentSuccess({
          orderId: data?.groceryOrderId,
          orderNumber:
            data?.displayOrderNo ||
            data?.display_order_no ||
            data?.displayOrderRef ||
            data?.groceryOrderId,
          isNewOrderPlaced: true,
          paymentMethod: snapshotPayment,
          lines: snapshotLines,
          totals: snapshotTotals,
        });
        resetAfterOrder();
        return;
      }

      alert(data?.message || 'Order saved successfully');
      resetAfterOrder();
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Could not save order.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveOrder = () => submitOrder('NEW');
  const handleCompleteOrder = () => submitOrder('CMP', { requirePayment: true });

  const renderCartBadge = () => (
    <div className="pos-header-cart" aria-label={`Cart: ${cartCount} items`}>
      <FaShoppingCart className="pos-header-cart-icon" />
      <span className="pos-header-cart-qty">{cartCount.toFixed(0)}</span>
    </div>
  );

  const renderQuantityInput = (item) => (
    <input
      type="number"
      className="quantity-input"
      value={item.quantity || ''}
      min="1"
      onChange={(e) => {
        const value = e.target.value;
        if (value === '') {
          setCart((prev) => ({
            ...prev,
            [String(item.groceryItemId)]: {
              ...prev[String(item.groceryItemId)],
              quantity: '',
            },
          }));
          return;
        }
        const newQty = parseInt(value, 10);
        if (!Number.isNaN(newQty) && newQty >= 1) {
          updateQuantity(item.groceryItemId, newQty);
        }
      }}
      onBlur={(e) => {
        const value = e.target.value;
        if (
          value === '' ||
          Number.isNaN(parseInt(value, 10)) ||
          parseInt(value, 10) < 1
        ) {
          updateQuantity(item.groceryItemId, 1);
        }
      }}
    />
  );

  const renderBillingSummary = (className = 'billing-summary') => (
    <div className={className}>
      <div className="billing-row">
        <span>Subtotal:</span>
        <span>₹{subtotal.toFixed(2)}</span>
      </div>
      <div className="billing-row">
        <span>GST ({TAX_PERCENT}%):</span>
        <span>₹{gst.toFixed(2)}</span>
      </div>
      <div className="billing-row total-row">
        <span>{className.includes('maximize') ? 'Total:' : 'Grand Total:'}</span>
        <span>₹{total.toFixed(2)}</span>
      </div>
    </div>
  );

  const renderPaymentTypeField = ({ useModalStyles = false } = {}) => {
    const dropdown = showPaymentTypeDropdown ? (
      <div
        className={
          useModalStyles
            ? 'pos-payment-type-dropdown pos-payment-type-dropdown--dropup'
            : undefined
        }
        style={
          useModalStyles
            ? undefined
            : {
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginBottom: '4px',
                zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                maxHeight: '200px',
                overflowY: 'auto',
              }
        }
      >
        <div
          className={useModalStyles ? 'pos-payment-type-dropdown__option' : undefined}
          onClick={() => {
            setPaymentType('');
            setShowPaymentTypeDropdown(false);
          }}
          style={
            useModalStyles
              ? undefined
              : {
                  padding: '12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                }
          }
          onMouseEnter={
            useModalStyles
              ? undefined
              : (e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }
          }
          onMouseLeave={
            useModalStyles
              ? undefined
              : (e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                }
          }
        >
          Select payment type
        </div>
        {PAYMENT_METHODS.map((method) => (
          <div
            key={method}
            className={useModalStyles ? 'pos-payment-type-dropdown__option' : undefined}
            onClick={() => {
              setPaymentType(method);
              setShowPaymentTypeDropdown(false);
            }}
            style={
              useModalStyles
                ? undefined
                : {
                    padding: '12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                  }
            }
            onMouseEnter={
              useModalStyles
                ? undefined
                : (e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }
            }
            onMouseLeave={
              useModalStyles
                ? undefined
                : (e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                  }
            }
          >
            {method.charAt(0).toUpperCase() + method.slice(1)}
          </div>
        ))}
      </div>
    ) : null;

    return (
      <div className="form-group payment-type-group">
        <label>Payment Type</label>
        <div
          style={{ position: 'relative' }}
          className={useModalStyles ? 'pos-payment-type-dropdown-wrap' : undefined}
          ref={paymentTypeDropdownRef}
        >
          <div
            role="button"
            tabIndex={0}
            className={`pos-payment-type-trigger${
              showPaymentTypeDropdown ? ' pos-payment-type-trigger--open' : ''
            }`}
            onClick={() => setShowPaymentTypeDropdown(!showPaymentTypeDropdown)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowPaymentTypeDropdown(!showPaymentTypeDropdown);
              }
            }}
          >
            <span
              className={
                !paymentType ? 'pos-payment-type-trigger-label--placeholder' : undefined
              }
            >
              {!paymentType
                ? 'Select payment type'
                : paymentType.charAt(0).toUpperCase() + paymentType.slice(1)}
            </span>
          </div>
          {dropdown}
        </div>
      </div>
    );
  };

  const renderCustomerFields = () => (
    <>
      {!showCustomerDetails ? (
        <button
          type="button"
          className="pos-customer-details-toggle pos-customer-details-toggle-add"
          onClick={() => setShowCustomerDetails(true)}
        >
          + Add customer details
        </button>
      ) : (
        <div className="pos-customer-details-panel">
          <button
            type="button"
            className="pos-customer-details-toggle pos-customer-details-toggle-hide"
            onClick={() => setShowCustomerDetails(false)}
          >
            Hide customer details
          </button>
          <div className="form-group">
            <label>Customer Name</label>
            <input
              type="text"
              placeholder="Enter customer name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Mobile Number</label>
            <input
              type="text"
              placeholder="Enter 10-digit mobile number"
              value={customerPhone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                setCustomerPhone(value);
              }}
              maxLength="10"
            />
          </div>
        </div>
      )}
    </>
  );

  const renderMaximizeGrocerySearch = () => (
    <div className="maximize-food-search-wrap">
      <div className="pos-header-food-search maximize-modal-food-search">
        <FaSearch className="search-icon-menu" />
        <input
          type="text"
          className="pos-header-search-input"
          placeholder="Search or scan barcode..."
          value={maximizeSearchQuery}
          onChange={(e) => setMaximizeSearchQuery(e.target.value)}
          onKeyDown={handleMaximizeSearchKeyDown}
          aria-label="Search or scan grocery barcode"
        />
        {maximizeSearchQuery.trim() ? (
          <button
            type="button"
            className="pos-header-search-clear"
            onClick={() => setMaximizeSearchQuery('')}
            aria-label="Clear search"
          >
            <FaTimes />
          </button>
        ) : null}
      </div>
      {maximizeSearchQuery.trim() ? (
        <div className="maximize-food-search-results" role="listbox" aria-label="Search results">
          {maximizeFilteredItems.length > 0 ? (
            <>
              <div className="maximize-food-search-results-header">
                <span className="maximize-food-search-results-count">
                  {maximizeFilteredItems.length} result
                  {maximizeFilteredItems.length !== 1 ? 's' : ''}
                </span>
              </div>
              <ul className="maximize-food-search-list">
                {maximizeFilteredItems.map((item) => {
                  const imageUrl = getStoreGroceryImageUrl(item);
                  const cartQty = cart[String(item.groceryItemId)]?.quantity ?? 0;
                  return (
                    <li
                      key={item.groceryItemId}
                      className="maximize-food-search-item"
                      role="option"
                    >
                      <div className="maximize-food-search-item-thumb">
                        <img
                          src={imageUrl}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = defaultGroceryImage;
                          }}
                        />
                      </div>
                      <div className="maximize-food-search-item-body">
                        <div className="maximize-food-search-item-name-row">
                          <button
                            type="button"
                            className="maximize-food-search-item-name buy-grocery-card-name-btn"
                            onClick={() => setDetailItem(item)}
                            title="View product details"
                          >
                            {item.itemName || 'Item'}
                          </button>
                          <GroceryCardPackQty item={item} />
                        </div>
                        <span className="maximize-food-search-item-price">
                          ₹{Number(item.sellingPrice || 0).toFixed(2)}
                        </span>
                      </div>
                      {cartQty > 0 ? (
                        <div className="buy-grocery-pos-qty maximize-food-search-qty">
                          <button
                            type="button"
                            className="buy-grocery-pos-qty-btn"
                            onClick={() => updateQuantity(item.groceryItemId, cartQty - 1)}
                            disabled={isSubmitting}
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <span className="buy-grocery-pos-qty-count">{cartQty}</span>
                          <button
                            type="button"
                            className="buy-grocery-pos-qty-btn"
                            onClick={() => addToCart(item)}
                            disabled={isSubmitting}
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="add-button maximize-food-search-add"
                          onClick={() => addToCart(item)}
                          disabled={isSubmitting}
                        >
                          Add
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <div className="maximize-food-search-empty">No grocery items found.</div>
          )}
        </div>
      ) : null}
    </div>
  );

  const renderResumeOrderNotice = () =>
    isCompletingSavedOrder ? (
      <p className="buy-grocery-status" style={{ marginBottom: '12px' }}>
        Completing order #{resumeOrderRef}. You can add more items, then select payment and click
        Complete.
      </p>
    ) : null;

  return (
    <div className="buy-grocery-page buy-grocery-page--pos-sidebar">
      <div className="buy-grocery-pos-layout">
        <div className="buy-grocery-pos-main">
          {storeId && canAccessOrdersTab() ? <StoreActiveOrders storeId={storeId} /> : null}

          {!storeId ? (
            <p className="buy-grocery-status">No store selected. Choose one from the profile menu.</p>
          ) : null}

          {scanFeedback ? (
            <p
              className={`buy-grocery-status pos-scan-feedback pos-scan-feedback--${scanFeedback.type}`}
              role="status"
            >
              {scanFeedback.message}
            </p>
          ) : null}

          <div className="buy-grocery-toolbar">
            <div className="content-header-actions pos-search-category-row buy-grocery-search-category-row">
              <div className="search-container search-container-pos">
                <FontAwesomeIcon icon={faSearch} className="search-icon-menu" aria-hidden />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search or scan barcode..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  aria-label="Search or scan grocery barcode"
                  autoComplete="off"
                />
                {searchQuery.trim() !== '' && (
                  <button
                    type="button"
                    className="pos-inline-search-clear"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>
              <div className="pos-category-select-wrap">
                <label className="pos-category-select-label" htmlFor="store-pos-category-select">
                  Category
                </label>
                <SearchableSelect
                  id="store-pos-category-select"
                  value={activeCategory}
                  onChange={(val) => {
                    setActiveCategory(val);
                    setSearchQuery('');
                  }}
                  options={categorySelectOptions}
                  placeholder="All categories"
                  searchPlaceholder="Search categories"
                  className="pos-category-searchable-select"
                  triggerClassName="pos-category-select-trigger"
                  minOptionsForSearch={1}
                  emptyMessage="No categories found"
                />
              </div>
            </div>
          </div>

          {loading ? <p className="buy-grocery-loading">Loading groceries…</p> : null}
          {!loading && error ? <p className="buy-grocery-status">{error}</p> : null}

          {!loading && !error && filteredItems.length === 0 ? (
            <p className="buy-grocery-empty">No grocery items found.</p>
          ) : (
            <div className="buy-grocery-grid">
              {filteredItems.map((item) => {
                const imageUrl = getStoreGroceryImageUrl(item);
                const inCart = cart[String(item.groceryItemId)]?.quantity ?? 0;
                return (
                  <article
                    key={item.groceryItemId}
                    className={`buy-grocery-card${inCart > 0 ? ' is-in-cart' : ''}`}
                  >
                    <div className="buy-grocery-card-image-wrap">
                      <img
                        src={imageUrl}
                        alt={item.itemName || 'Grocery item'}
                        className="buy-grocery-card-image"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = defaultGroceryImage;
                        }}
                      />
                    </div>
                    <div className="buy-grocery-card-body">
                      <div className="buy-grocery-card-name-row">
                        <button
                          type="button"
                          className="buy-grocery-card-name buy-grocery-card-name-btn"
                          onClick={() => setDetailItem(item)}
                          title="View product details"
                        >
                          {item.itemName || 'Item'}
                        </button>
                        <GroceryCardPackQty item={item} />
                      </div>
                      <div className="buy-grocery-card-footer">
                        <GroceryCardPrice item={item} />
                        <div className="buy-grocery-card-action">
                        {inCart > 0 ? (
                          <div className="buy-grocery-pos-qty">
                            <button
                              type="button"
                              className="buy-grocery-pos-qty-btn"
                              onClick={() => updateQuantity(item.groceryItemId, inCart - 1)}
                              aria-label="Decrease quantity"
                              disabled={isSubmitting}
                            >
                              −
                            </button>
                            <span className="buy-grocery-pos-qty-count">{inCart}</span>
                            <button
                              type="button"
                              className="buy-grocery-pos-qty-btn"
                              onClick={() => addToCart(item)}
                              aria-label="Increase quantity"
                              disabled={isSubmitting}
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="buy-grocery-card-add"
                            onClick={() => addToCart(item)}
                            disabled={isSubmitting}
                          >
                            Add
                          </button>
                        )}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        <div className="order-sidebar">
          <div className="order-sidebar-header">
            <div className="sidebar-header-top pos-sidebar-header-row">
              <h3 className="pos-sidebar-title">Order Details</h3>
              {!showMaximizeModal ? (
                <div className="pos-table-cart-group">{renderCartBadge()}</div>
              ) : null}
              <button
                type="button"
                className="control-button maximize-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowMaximizeModal(true);
                }}
                title="Open in window"
              >
                <FaExternalLinkAlt style={{ fontSize: '14px' }} />
              </button>
            </div>
          </div>

          <div className="order-sidebar-body">
            {renderResumeOrderNotice()}
            {resumeOrderLoading ? (
              <p className="buy-grocery-loading">Loading order…</p>
            ) : null}
            <div className="ordered-items-section">
              <table className="ordered-items-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Price</th>
                    <th>Quantity</th>
                    <th>Total</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {cartLines.length > 0 ? (
                    cartLines.map((item) => (
                      <tr key={item.groceryItemId}>
                        <td>{item.itemName}</td>
                        <td>₹{parseFloat(item.unitPrice || 0).toFixed(2)}</td>
                        <td>
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {renderQuantityInput(item)}
                          </div>
                        </td>
                        <td>
                          <span>
                            ₹
                            {(
                              parseFloat(item.unitPrice || 0) * Number(item.quantity || 0)
                            ).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="remove-button"
                            onClick={() => removeFromCart(item.groceryItemId)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                        No items in cart
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {renderBillingSummary()}

            <div className="customer-details">
              {renderPaymentTypeField()}
              {renderCustomerFields()}
            </div>

            <div className="order-action-buttons">
              <button
                type="button"
                className="save-order-button"
                onClick={handleSaveOrder}
                disabled={cartLines.length === 0 || isSubmitting || resumeOrderLoading}
              >
                Save
              </button>
              <button
                type="button"
                className="place-order-button"
                onClick={handleCompleteOrder}
                disabled={cartLines.length === 0 || isSubmitting || resumeOrderLoading}
              >
                Complete
              </button>
            </div>
          </div>
        </div>
      </div>

      {showMaximizeModal ? (
        <div className="maximize-modal-overlay" onClick={() => setShowMaximizeModal(false)}>
          <div className="maximize-modal" onClick={(e) => e.stopPropagation()}>
            <div className="maximize-modal-header-section">
              <div className="maximize-modal-header">
                <h3>Order Details</h3>
                {renderMaximizeGrocerySearch()}
                <div className="maximize-modal-controls">
                  {renderCartBadge()}
                  <button
                    type="button"
                    className="maximize-modal-close"
                    onClick={() => setShowMaximizeModal(false)}
                    title="Close"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            </div>

            {isCompletingSavedOrder ? (
              <div
                style={{
                  padding: '15px 30px',
                  backgroundColor: '#f0f0f0',
                  borderBottom: '1px solid #e0e0e0',
                  fontSize: '14px',
                  color: '#333',
                }}
              >
                Completing order #{resumeOrderRef}
              </div>
            ) : null}

            <div
              className={`maximize-modal-content${
                showPaymentTypeDropdown ? ' maximize-modal-content--payment-open' : ''
              }`}
            >
              <div className="maximize-order-summary">
                <h4>Order Summary</h4>
                <div className="maximize-ordered-items-section">
                  <table className="maximize-ordered-items-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Price</th>
                        <th>Quantity</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartLines.length > 0 ? (
                        cartLines.map((item) => (
                          <tr key={item.groceryItemId}>
                            <td>{item.itemName}</td>
                            <td>₹{parseFloat(item.unitPrice || 0).toFixed(2)}</td>
                            <td>{renderQuantityInput(item)}</td>
                            <td>
                              <div className="total-with-action">
                                <span>
                                  ₹
                                  {(
                                    parseFloat(item.unitPrice || 0) * Number(item.quantity || 0)
                                  ).toFixed(2)}
                                </span>
                                <button
                                  type="button"
                                  className="remove-button"
                                  onClick={() => removeFromCart(item.groceryItemId)}
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                            No items in cart
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {renderBillingSummary('maximize-billing-summary')}
              </div>

              <div
                className={`maximize-customer-details${
                  showPaymentTypeDropdown ? ' maximize-customer-details--payment-open' : ''
                }`}
              >
                <h4>Customer Details</h4>
                {renderPaymentTypeField({ useModalStyles: true })}
                {renderCustomerFields()}
                <div className="maximize-order-total">
                  <div className="billing-row total-row">
                    <span>Grand Total:</span>
                    <span>₹{total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="maximize-modal-footer">
              <button
                type="button"
                className="save-order-button-modal"
                onClick={() => {
                  handleSaveOrder();
                  setShowMaximizeModal(false);
                }}
                disabled={cartLines.length === 0 || isSubmitting || resumeOrderLoading}
              >
                Save
              </button>
              <button
                type="button"
                className="place-order-button-modal"
                onClick={() => {
                  handleCompleteOrder();
                  setShowMaximizeModal(false);
                }}
                disabled={cartLines.length === 0 || isSubmitting || resumeOrderLoading}
              >
                Complete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showOrderConfirmation ? (
        <OrderConfirmation
          orderData={orderConfirmationData}
          onClose={() => {
            setShowOrderConfirmation(false);
            setOrderConfirmationData(null);
          }}
        />
      ) : null}

      {detailItem ? (
        <div
          className="buy-grocery-detail-overlay"
          role="presentation"
          onClick={() => setDetailItem(null)}
        >
          <div
            className="buy-grocery-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="store-pos-item-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="buy-grocery-detail-header">
              <h3 id="store-pos-item-detail-title">Product details</h3>
              <button
                type="button"
                className="buy-grocery-detail-close"
                onClick={() => setDetailItem(null)}
                aria-label="Close product details"
              >
                <FaTimes />
              </button>
            </div>
            {(() => {
              const imageUrl = getStoreGroceryImageUrl(detailItem);
              const priceParts = getGroceryItemPriceParts(detailItem);
              const stockRaw =
                detailItem.stockQuantity ?? detailItem.quantityInStock;
              const stockNum = Number(stockRaw);
              const inCart =
                cart[String(detailItem.groceryItemId)]?.quantity ?? 0;
              return (
                <>
                  <div className="buy-grocery-detail-body">
                    <div className="buy-grocery-detail-image-wrap">
                      <img
                        src={imageUrl}
                        alt={detailItem.itemName || 'Grocery item'}
                        className="buy-grocery-detail-image"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = defaultGroceryImage;
                        }}
                      />
                    </div>
                    <div className="buy-grocery-detail-main">
                      <div className="buy-grocery-detail-title-row">
                        <h4 className="buy-grocery-detail-name">
                          {detailItem.itemName || 'Item'}
                        </h4>
                        {priceParts?.quantityLabel ? (
                          <span className="buy-grocery-card-pack-qty">
                            {priceParts.quantityLabel}
                          </span>
                        ) : null}
                      </div>
                      <div className="buy-grocery-detail-price-row">
                        <span className="buy-grocery-card-selling-price">
                          {priceParts?.sellingPrice ||
                            formatGroceryRupee(detailItem.sellingPrice) ||
                            '—'}
                        </span>
                        {priceParts?.showMrp && priceParts.mrp ? (
                          <span className="buy-grocery-card-mrp">{priceParts.mrp}</span>
                        ) : null}
                      </div>
                      <dl className="buy-grocery-detail-meta">
                        <div>
                          <dt>Category</dt>
                          <dd>{detailValue(detailItem.groceryCategoryName)}</dd>
                        </div>
                        <div>
                          <dt>Brand</dt>
                          <dd>{detailValue(detailItem.brandName)}</dd>
                        </div>
                        <div>
                          <dt>Barcode</dt>
                          <dd>{detailValue(detailItem.barcode)}</dd>
                        </div>
                        <div>
                          <dt>In stock</dt>
                          <dd>
                            {stockRaw != null && Number.isFinite(stockNum)
                              ? stockNum
                              : detailValue(stockRaw)}
                          </dd>
                        </div>
                      </dl>
                      <div className="buy-grocery-detail-desc">
                        <span className="buy-grocery-detail-desc-label">Description</span>
                        <p>
                          {String(detailItem.description || '').trim() ||
                            'No description available.'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="buy-grocery-detail-footer">
                    {inCart > 0 ? (
                      <div className="buy-grocery-pos-qty">
                        <button
                          type="button"
                          className="buy-grocery-pos-qty-btn"
                          onClick={() =>
                            updateQuantity(detailItem.groceryItemId, inCart - 1)
                          }
                          disabled={isSubmitting}
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        <span className="buy-grocery-pos-qty-count">{inCart}</span>
                        <button
                          type="button"
                          className="buy-grocery-pos-qty-btn"
                          onClick={() => addToCart(detailItem)}
                          disabled={isSubmitting}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="buy-grocery-card-add"
                        onClick={() => addToCart(detailItem)}
                        disabled={isSubmitting}
                      >
                        Add to cart
                      </button>
                    )}
                    <button
                      type="button"
                      className="buy-grocery-detail-done"
                      onClick={() => setDetailItem(null)}
                    >
                      Close
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default StorePos;
